import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import DayPicker from "react-day-picker";
import {getDateRange, getInterval, getOptionInterval} from "../../helpers/DateHelper";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

function DeadlineDatepickerModal({exactDeadline, setExactDeadline, cancelModal, deadlines, numberOfMonths}) {
    const {dateFrom, dateTo} = getDateRange(exactDeadline);

    const [from, setFrom] = useState(dateFrom);
    const [to, setTo] = useState(dateTo);

    const [deadlineInterval, setDeadlineInterval] = useState(getOptionInterval(dateFrom, dateTo, deadlines));
    const [exactDeadlineInterval, setExactDeadlineInterval] = useState(getInterval(dateFrom, dateTo));

    const [isShown, setIsShown] = useState(!(deadlines.find(deadline => deadline.value === exactDeadline)));

    const [selectedTime, setSelectedTime] = useState(null);
    const [hasSelected, setHasSelected] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);

    const getMinSelectableDateTime = () => {
        const selected = deadlines[deadlines.length - 1];
        const minHours = selected && selected.is3h ? 3 : 24;
        return dayjs().add(minHours, "hour");
    };

    const [minSelectableDateTime, setMinSelectableDateTime] = useState(getMinSelectableDateTime);

    const minSelectableDate = minSelectableDateTime.startOf('day').toDate();

    useEffect(() => {
        setDeadlineInterval(getOptionInterval(from, to, deadlines));
        setExactDeadlineInterval(getInterval(from, to));
    }, [from, to, deadlines])

    useEffect(() => {
        const selected = deadlines[deadlines.length - 1];
        const minHours = selected && selected.is3h ? 3 : 24;
        setMinSelectableDateTime(dayjs().add(minHours, "hour"));
    }, [exactDeadline, isShown, deadlines]);

    const onDayClick = (day, modifiers) => {
        if (modifiers.disabled) {
            return;
        }

        const now = new Date();

        const normalizedTo = normalizeDateTo(day);

        setFrom(now);
        setTo(normalizedTo);
        setSelectedDay(day);
        setHasSelected(true);
    }

    const selectDeadline = value => {
        const {dateFrom, dateTo} = getDateRange(value);

        setFrom(dateFrom);
        setTo(dateTo);

        setExactDeadline(getInterval(dateFrom, dateTo));
        setIsShown(false);
        setHasSelected(false);
        cancelModal();
    }

    const handleApply = () => {
        if (hasSelected) {
            setExactDeadline(exactDeadlineInterval);
        }
        cancelModal();
    }

    const renderDay = day => {
        return <div>{day.getDate()}</div>
    }

    const normalizeDateTo = dateTo => {
        const minDateTo = new Date();
        minDateTo.setSeconds(minDateTo.getSeconds() + 86400);

        if (minDateTo > dateTo) {
            return minDateTo;
        }

        return dateTo;
    }

    const generateTimeList = () => {
        const times = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 15) {
                times.push(dayjs().hour(h).minute(m).format("hh:mm A"));
            }
        }
        return times;
    };

    const isTimeSelectable = (time) => {
        if (!hasSelected || !selectedDay) return false;
        const selectedDate = dayjs(selectedDay).format("YYYY-MM-DD");
        const selectedDateTime = dayjs(`${selectedDate} ${time}`, "YYYY-MM-DD hh:mm A");
        return selectedDateTime.isAfter(minSelectableDateTime);
    };

    const timeList = generateTimeList();

    const onTimeClick = (selectedTimeValue) => {
        setSelectedTime(selectedTimeValue);

        const currentDateStr = dayjs().format("YYYY-MM-DD");
        const parsedTime = dayjs(`${currentDateStr} ${selectedTimeValue}`, "YYYY-MM-DD hh:mm A");

        if (!parsedTime.isValid()) {
            return;
        }

        const updatedTo = dayjs(to)
            .hour(parsedTime.hour())
            .minute(parsedTime.minute())
            .second(0)
            .millisecond(0)
            .toDate();

        setTo(updatedTo);

        const intervalInSeconds = Math.round((updatedTo.getTime() - from.getTime()) / 1000);
        if (intervalInSeconds > 0) {
            setExactDeadline(intervalInSeconds);
        }
    };

    return <div className="datepicker__modal">
        <ul className="time-lists">
            {deadlines.map(deadline => (
                <li
                    key={deadline.value}
                    className={deadlineInterval == deadline.value && !isShown ? 'active' : ''}
                    onClick={() => selectDeadline(deadline.value)}
                >
                    {deadline.label}
                </li>
            ))}
            <li
                className={`custom-time-lists ${isShown ? 'active' : ''}`}
                onClick={() => setIsShown(true)}>
                <svg width="12" height="15" viewBox="0 0 12 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.66667 7.59672H4V8.93006H2.66667V7.59672ZM12 3.59672V12.9301C12 13.2837 11.8595 13.6228 11.6095 13.8729C11.3594 14.1229 11.0203 14.2634 10.6667 14.2634H1.33333C0.979711 14.2634 0.640573 14.1229 0.390524 13.8729C0.140476 13.6228 0 13.2837 0 12.9301V3.59672C0 3.2431 0.140476 2.90396 0.390524 2.65391C0.640573 2.40386 0.979711 2.26339 1.33333 2.26339H2V0.930054H3.33333V2.26339H8.66667V0.930054H10V2.26339H10.6667C11.0203 2.26339 11.3594 2.40386 11.6095 2.65391C11.8595 2.90396 12 3.2431 12 3.59672ZM1.33333 4.93005H10.6667V3.59672H1.33333V4.93005ZM10.6667 12.9301V6.26339H1.33333V12.9301H10.6667ZM8 8.93006V7.59672H9.33333V8.93006H8ZM5.33333 8.93006V7.59672H6.66667V8.93006H5.33333ZM2.66667 10.2634H4V11.5967H2.66667V10.2634ZM8 11.5967V10.2634H9.33333V11.5967H8ZM5.33333 11.5967V10.2634H6.66667V11.5967H5.33333Z" fill="#B5B8C2" />
                </svg>
                Custom
            </li>
        </ul>
        {
            isShown &&
            <React.Fragment>
                <div className="daypicker__box">
                    <DayPicker
                        className="datepicker__calendar"
                        selectedDays={hasSelected ? selectedDay : null}
                        numberOfMonths={numberOfMonths ? numberOfMonths : 1}
                        onDayClick={onDayClick}
                        modifiers={hasSelected ? {selected: to} : {}}
                        fromMonth={from}
                        disabledDays={{before: minSelectableDate}}
                        renderDay={renderDay}
                        fixedWeeks
                        pagedNavigation
                    />
                    <div className="react-datepicker__time-container">
                        <div className="react-datepicker__header react-datepicker__header--time">
                            <div className="react-datepicker-time__header">Time</div>
                        </div>
                        <div className="react-datepicker__time">
                            <div className="react-datepicker__time-box">
                                <ul className="react-datepicker__time-list" tabIndex="0">
                                    {timeList.map((time, index) => {
                                        const isDisabled = !isTimeSelectable(time);
                                        return (
                                            <li
                                                key={index}
                                                className={`react-datepicker__time-list-item ${selectedTime === time ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                                                style={{
                                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                                }}
                                                tabIndex={isDisabled ? -1 : 0}
                                                onClick={() => {
                                                    if (!isDisabled) onTimeClick(time);
                                                }}
                                            >
                                                <span style={{ color: isDisabled ? "#cecece" : "" }}>{time}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="datepicker__buttons">
                    <button className="datepicker__button datepicker__button--cancel" type="button" onClick={cancelModal}>Cancel</button>
                    <button className="datepicker__button datepicker__button--apply" type="button" disabled={!hasSelected} onClick={handleApply}>Apply</button>
                </div>
            </React.Fragment>
        }
    </div>
}

DeadlineDatepickerModal.propTypes = {
    exactDeadline: PropTypes.number.isRequired,
    setExactDeadline: PropTypes.func.isRequired,
    cancelModal: PropTypes.func.isRequired,
    deadlines: PropTypes.array.isRequired,
};

export default DeadlineDatepickerModal;
